import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Check, X, RefreshCw, AlertTriangle, FileCog, Database, ShieldOff, Shield } from 'lucide-react';
import { localDevApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';

/**
 * Local Dev tab — dev-VM-only window onto appsettings.Development.json.
 * Status is read from /api/localdev/config (backend 404s outside local dev).
 * Writes are limited to the DataSync subtree; AzureAd / CORS / connection
 * string VALUES are never shown or writable (HANDS OFF — see CLAUDE.md).
 *
 * EDIT SEMANTICS (Josh 2026-08-24): follows the page-wide CRUD standard —
 * nothing is written until the page is in edit mode (pencil), a change is
 * staged, and Save is clicked. Draft applies via the saveRef handle; leaving
 * edit mode discards it.
 *
 * LAYOUT (Josh 2026-08-24): one row per item — environments, phases, sync
 * directions, connections, services each get their own labeled status row.
 */

const StatusIcon = ({ ok }) => ok
  ? <Check size={14} className="stui-shrink-0" style={{ color: 'var(--color-success, #16a34a)' }} />
  : <X size={14} className="stui-shrink-0" style={{ color: 'var(--color-error, #dc2626)' }} />;

const SectionHeader = ({ title }) => (
  <div className="stui-detail-section-header"><span>{title}</span></div>
);

/** One labeled status row: name + optional sub on the left, icon + detail on the right. */
const StatusRow = ({ label, sub, ok, detail, icon: Icon }) => (
  <div className="stui-row-setting">
    <div className="stui-row-setting-label">
      <div className="stui-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
        {Icon && <Icon size={13} />} {label}
      </div>
      {sub && <div className="stui-sublabel">{sub}</div>}
    </div>
    <div className="stui-row-setting-controls" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
      {ok !== undefined && <StatusIcon ok={ok} />}
      <span className="stui-sublabel">{detail}</span>
    </div>
  </div>
);

const LocalDevTab = ({ isEditing = false, onDirtyChange, saveRef }) => {
  const { notify } = useNotifications();
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Staged, unsaved changes. enabled:null = follow the file; seed:false = not requested.
  const [draft, setDraft] = useState({ enabled: null, seed: false });

  const load = useCallback(() => {
    localDevApi.getConfig()
      .then(res => { setCfg(res.data); setLoadError(null); })
      .catch(err => setLoadError(err.response?.status === 404
        ? 'Local dev config is not served here (not a dev environment).'
        : (err.response?.data?.error || err.message)));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Re-read the file whenever the window regains focus — hand-edits in an
  // external editor show up when you alt-tab back, no refresh needed.
  useEffect(() => {
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load]);

  const ds = cfg?.dataSync || {};
  const adminSync = cfg?.adminSync || {};
  const fileEnabled = !!ds.fileEnabled;
  const shownEnabled = draft.enabled ?? fileEnabled;
  const canSeed = !ds.fileHasSection || (ds.environments || []).length === 0;

  const dirty = useMemo(
    () => (draft.enabled !== null && draft.enabled !== fileEnabled) || draft.seed,
    [draft, fileEnabled]
  );

  // Report dirtiness to the page header (Save button state); clear on unmount.
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Leaving edit mode (Cancel) discards the staged draft.
  useEffect(() => { if (!isEditing) setDraft({ enabled: null, seed: false }); }, [isEditing]);

  // Expose the save routine to the page header via ref handle.
  useEffect(() => {
    if (!saveRef) return undefined;
    saveRef.current = !dirty ? null : async () => {
      if (draft.seed) await localDevApi.writeConfig('seedDataSyncTemplate');
      if (draft.enabled !== null && draft.enabled !== fileEnabled) {
        await localDevApi.writeConfig('setDataSyncEnabled', draft.enabled);
      }
      setDraft({ enabled: null, seed: false });
      load();
      notify.info('appsettings.Development.json written — restart the backend (dotnet run) to apply.');
    };
    return () => { saveRef.current = null; };
  }, [saveRef, dirty, draft, fileEnabled, load, notify]);

  if (loadError) {
    return <div className="stui-label-note stui-text-error">{loadError}</div>;
  }
  if (!cfg) {
    return <div className="stui-label-note">Loading local config…</div>;
  }
  if (cfg.fileError) {
    return (
      <div className="stui-status-text stui-status-text-error">
        <AlertTriangle size={14} /> {cfg.fileError}
      </div>
    );
  }

  return (
    <>
      <div className="stui-label-note">
        This tab reads and writes <code>{cfg.filePath}</code> — the gitignored per-VM file that
        controls local functionality. It exists only on dev VMs; ppdawn/dawn never serve it.
        Auth settings and connection string values are display-status only (HANDS OFF).
        Edits stage like any other preference — pencil to edit, Save to write the file, then
        restart the backend.
      </div>

      {/* ── Authentication ── */}
      <SectionHeader title="Authentication" />
      <StatusRow
        label="AzureAd ClientId"
        sub="'placeholder' disables local auth enforcement — the required state for dev VMs"
        ok={cfg.authMode === 'placeholder'}
        detail={cfg.authMode === 'placeholder' ? 'placeholder' : 'real GUID — see warning below'}
      />
      <StatusRow
        label="Local Auth Enforcement"
        icon={cfg.authMode === 'placeholder' ? ShieldOff : Shield}
        detail={cfg.authMode === 'placeholder'
          ? 'OFF — impersonation mode, no JWT required'
          : 'ON — real Entra tokens enforced'}
      />
      <StatusRow label="ASPNETCORE_ENVIRONMENT" detail={cfg.environment} />
      {cfg.authMode !== 'placeholder' && (
        <div className="stui-status-text stui-status-text-error">
          <AlertTriangle size={14} /> A real ClientId in appsettings.Development.json breaks every other
          dev box if committed. Use env-var overrides for Entra testing (see documents/claude/notes/environment.md).
        </div>
      )}

      {/* ── Data Sync ── */}
      <SectionHeader title="Data Sync (Data / Schema / View Sync)" />
      {ds.restartRequired && !dirty && (
        <div className="stui-label-note" style={{ color: 'var(--color-warning, #b45309)' }}>
          <RefreshCw size={14} /> File and running process disagree — restart the backend
          (<code>dotnet run</code>) to load the on-disk configuration.
        </div>
      )}
      <div className="stui-row-setting">
        <div className="stui-row-setting-label">
          <div className="stui-label">Sync Tools Enabled</div>
          <div className="stui-sublabel">
            DataSync:Enabled — master switch. The gate also needs the environment configuration
            below before sync pages open.
          </div>
        </div>
        <label className="stui-toggle">
          <input
            type="checkbox"
            checked={shownEnabled}
            disabled={!isEditing}
            onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))}
          />
          <span className="stui-toggle-slider" />
        </label>
      </div>

      <div className="stui-row-setting">
        <div className="stui-row-setting-label">
          <div className="stui-label">{canSeed ? 'Generate DataSync Configuration' : 'Regenerate DataSync Configuration'}</div>
          <div className="stui-sublabel">
            {canSeed
              ? "This file has no DataSync environment configuration yet, so the sync tools can't run here even when enabled. Generate builds it automatically from the connection strings already in the file (AWA + PreProd, credentials included). Prod can't be derived locally — its entries are placeholders until prod credentials are pasted into the file."
              : 'Rebuilds the DataSync environments from the connection strings in this file. Safe to re-run — real credentials already in the section (e.g. hand-pasted prod) are always kept; only missing or placeholder values are filled. Use this when onboarding a new dev VM or after connection strings change.'}
          </div>
        </div>
        <div className="stui-row-setting-controls">
          {draft.seed && <span className="stui-sublabel">queued — applies on Save</span>}
          <button
            className={`stui-btn ${draft.seed ? 'stui-btn-secondary' : 'stui-btn-primary'}`}
            disabled={!isEditing}
            onClick={() => setDraft(d => ({ ...d, seed: !d.seed }))}
          >
            <FileCog size={13} /> {draft.seed ? 'Undo' : (canSeed ? 'Generate' : 'Regenerate')}
          </button>
        </div>
      </div>

      {(ds.environments || []).length > 0 && (
        <>
          {(ds.environments || []).map(e => (
            <StatusRow
              key={e.key}
              label={e.displayName || e.key}
              sub="sync environment"
              ok={e.available}
              detail={e.available ? 'configured' : (e.reason || 'not configured')}
            />
          ))}
          {(ds.phases || []).map(p => (
            <StatusRow
              key={p.key}
              label={p.displayName || p.key}
              sub="sync phase"
              ok={p.available}
              detail={p.available ? 'ready' : (p.reason || 'unavailable')}
            />
          ))}
        </>
      )}

      {/* ── Admin Config Sync ── */}
      <SectionHeader title="Admin Config Sync" />
      <div className="stui-label-note">
        Separate gate from Data Sync: it reconciles the preprod and prod ADMIN databases, so it
        needs an admin connection for BOTH sides. The prod admin connection cannot be derived
        from this file — paste it into DataSync:Environments:prod:AdminConnectionString by hand
        to unlock.
      </div>
      <StatusRow
        label="Admin Config Sync"
        ok={adminSync.available}
        detail={adminSync.available ? 'available' : (adminSync.reason || 'unavailable')}
      />
      {(adminSync.directions || []).map(d => (
        <StatusRow
          key={d.key}
          label={d.displayName || d.key}
          sub="sync direction"
          ok={d.available}
          detail={d.available ? 'ready' : (d.reason || 'unavailable')}
        />
      ))}

      {/* ── Connections ── */}
      <SectionHeader title="Connection Strings" />
      <div className="stui-label-note">
        Names, servers, and catalogs only — credentials never leave the file.
      </div>
      {(cfg.connections || []).length === 0 ? (
        <div className="stui-status-text stui-status-text-error">
          <AlertTriangle size={14} /> No ConnectionStrings section found — the backend cannot reach any database.
        </div>
      ) : (
        cfg.connections.map(c => (
          <StatusRow
            key={c.name}
            label={c.name}
            icon={Database}
            detail={`${c.server || '?'}  /  ${c.catalog || '?'}`}
          />
        ))
      )}

      {/* ── Services ── */}
      <SectionHeader title="Services" />
      <StatusRow
        label="Blob Storage"
        sub="org logos / image uploads"
        ok={cfg.blobConfigured}
        detail={cfg.blobConfigured ? 'configured' : 'not configured'}
      />
      <StatusRow
        label="Graph Email"
        sub="user invitation emails"
        ok={cfg.graphConfigured}
        detail={cfg.graphConfigured ? 'configured' : 'stub — invite links copied manually'}
      />
    </>
  );
};

export default LocalDevTab;
